import { useState, useEffect } from 'react';
import { QrCode, Globe, History, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import QRGenerator from './QRGenerator';
import QRTypeSelector from './QRTypeSelector';
import QRCustomizer from './QRCustomizer';
import QRHistory from './QRHistory';
import LanguageToggle from './LanguageToggle';
import NavigationMenu from './NavigationMenu';
import SEOContent from './SEOContent';
import Footer from './Footer';
import { QRCodeData, QRCodeType, QRHistoryItem } from '../types/qr-types';
import { blogArticles } from '../data/blogArticles';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';
import { usePreconnect, useDNSPrefetch } from '../hooks/usePreload';

function MainContent() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<'generate' | 'history'>('generate');
  const [qrType, setQrType] = useState<QRCodeType>('url');
  const [qrData, setQrData] = useState<QRCodeData>({
    type: 'url',
    url: '',
    color: '#000000',
    backgroundColor: '#ffffff',
    size: 256,
    errorLevel: 'M'
  });
  const [generatedQRs, setGeneratedQRs] = useState<QRHistoryItem[]>([]);
  const [showCustomizer, setShowCustomizer] = useState(false);

  // 性能优化: 预连接到外部服务
  usePreconnect([
    'https://www.googletagmanager.com',
    'https://www.google-analytics.com'
  ]);

  // 预解析外部域名
  useDNSPrefetch([
    'https://cdn.jsdelivr.net',
    'https://unpkg.com'
  ]);

  // 从localStorage加载历史记录
  useEffect(() => {
    const savedHistory = localStorage.getItem('qr-history');
    if (savedHistory) {
      try {
        setGeneratedQRs(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Failed to load QR history:', error);
      }
    }
  }, []);

  // 保存历史记录到localStorage
  const saveToHistory = (historyItem: QRHistoryItem) => {
    const newHistory = [historyItem, ...generatedQRs.slice(0, 49)]; // 保留最近50条记录
    setGeneratedQRs(newHistory);
    localStorage.setItem('qr-history', JSON.stringify(newHistory));
  };

  // 清空历史记录
  const clearHistory = () => {
    setGeneratedQRs([]);
    localStorage.removeItem('qr-history');
  };

  // 设置页面标题和meta描述
  useEffect(() => {
    const title = t('meta.title', language);
    const description = t('meta.description', language);
    const keywords = t('meta.keywords', language);
    
    document.title = title;
    
    // 更新meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // 更新meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);

    // 更新canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `https://www.qrcodeforge.com/${language}`;

    // 更新hreflang
    const updateHrefLang = (lang: string, url: string) => {
      let hrefLangLink = document.querySelector(`link[hreflang="${lang}"]`) as HTMLLinkElement;
      if (!hrefLangLink) {
        hrefLangLink = document.createElement('link');
        hrefLangLink.rel = 'alternate';
        hrefLangLink.hreflang = lang;
        document.head.appendChild(hrefLangLink);
      }
      hrefLangLink.href = url;
    };

    updateHrefLang('en', 'https://www.qrcodeforge.com/en');
    updateHrefLang('zh', 'https://www.qrcodeforge.com/zh');
    updateHrefLang('x-default', 'https://www.qrcodeforge.com/en');
  }, [language]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-50">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Fixed Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-900/80 via-purple-900/80 to-slate-900/80 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-xl font-bold text-white">
                  {t('title', language)}
                </h1>
                <div className="flex items-center gap-2 text-xs text-gray-300">
                  <Globe className="w-3 h-3" />
                  <span>www.qrcodeforge.com</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <NavigationMenu />
              <LanguageToggle />
            </div>
          </div>
        </header>

        {/* Main Content Area with top padding */}
        <div className="pt-20">
          {/* Subtitle */}
          <div className="text-center py-8 px-4">
            <div className="max-w-2xl mx-auto">
              <p className="text-lg text-gray-300">
                {t('subtitle', language)}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center mb-8 px-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-1 flex">
              <button
                onClick={() => setActiveTab('generate')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === 'generate'
                    ? 'bg-white text-gray-900 shadow-lg'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <QrCode className="w-4 h-4" />
                {t('generate', language)}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === 'history'
                    ? 'bg-white text-gray-900 shadow-lg'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <History className="w-4 h-4" />
                {t('history', language)}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 pb-12">
          
          {activeTab === 'generate' ? (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - QR Type & Customization */}
              <div className="lg:col-span-1 space-y-6">
                <QRTypeSelector
                  selectedType={qrType}
                  onTypeChange={(type) => {
                    setQrType(type);
                    setQrData(prev => ({ ...prev, type }) as QRCodeData);
                  }}
                  language={language}
                />
                
                <QRCustomizer
                  qrData={qrData}
                  onDataChange={setQrData}
                  language={language}
                  showCustomizer={showCustomizer}
                  onToggleCustomizer={() => setShowCustomizer(!showCustomizer)}
                />
              </div>

              {/* Right Columns - QR Generator */}
              <div className="lg:col-span-2">
                <QRGenerator
                  qrData={qrData}
                  onDataChange={setQrData}
                  onSave={saveToHistory}
                  language={language}
                />
              </div>
            </div>
          ) : (
            <QRHistory
              history={generatedQRs}
              onClearHistory={clearHistory}
              onRegenerate={(item) => {
                setQrData(item);
                setQrType(item.type);
                setActiveTab('generate');
              }}
            />
          )}
        </div>

        {/* Latest Updates Section */}
        <div className="max-w-7xl mx-auto px-4 pb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white">
              {language === 'zh' ? '最新动态' : 'Latest Updates'}
            </h2>
            <Link 
              to={`/${language}/blog`}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              {language === 'zh' ? '查看全部' : 'View All'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {Object.values(blogArticles[language] || blogArticles['en'])
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .slice(0, 3)
              .map((article: any) => (
              <Link 
                key={article.title}
                to={`/${language}/blog/${Object.keys(blogArticles[language] || blogArticles['en']).find(key => (blogArticles[language] || blogArticles['en'])[key] === article)}`}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/15 transition-all group"
              >
                <div className="text-sm text-blue-400 mb-3">
                  {new Date(article.date).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-300 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-400 text-sm line-clamp-3">
                  {article.excerpt}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* SEO Content */}
        <SEOContent />

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}

export default MainContent;